import React, { useState, useEffect, useCallback } from 'react';
import { reviewService } from '../../services/reviewService';
import { toast } from 'react-toastify';

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
        <p className="text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors duration-200"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors duration-200"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewManager = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [viewMode, setViewModeState] = useState(() => {
    try { return localStorage.getItem("adminViewMode") || "cards"; } catch { return "cards"; }
  });
  const setViewMode = (v) => { setViewModeState(v); try { localStorage.setItem("adminViewMode", v); } catch (_) {} };

  const fetchReviews = useCallback(async (page, limit) => {
    try {
      setLoading(true);
      // Fetch all reviews, regardless of status
      const response = await reviewService.admin.getAllReviews({ page, limit });
      console.log('API Response:', response); // Log the full API response
      
      // Access the nested data structure
      const data = response.data || response;
      setReviews(data.reviews || []);
      if (data.pagination) {
        setPagination(data.pagination);
      }
      setError(null);
    } catch (err) {
      setError('Erreur lors de la recuperation des avis.');
      toast.error('Erreur lors de la recuperation des avis.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews(pagination.page, pagination.limit);
  }, [fetchReviews, pagination.page, pagination.limit]);

  const handleApprove = async (id) => {
    try {
      await reviewService.admin.updateReviewStatus(id, 'approuve');
      toast.success('Avis approuve avec succes.');
      setReviews(reviews.map(r => r._id === id ? { ...r, statut: 'approuve' } : r));
    } catch (err) {
      toast.error('Erreur lors de l approbation de l avis.');
    }
  };

  const handleDeleteClick = (id) => {
    setReviewToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (reviewToDelete) {
      try {
        await reviewService.admin.deleteReview(reviewToDelete);
        toast.success('Avis supprimé avec succès.');
        fetchReviews(pagination.page, pagination.limit);
      } catch (err) {
        toast.error('Erreur lors de la suppression de l\'avis.');
      }
    }
    setShowDeleteModal(false);
    setReviewToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setReviewToDelete(null);
  };

  const VIEW_OPTIONS = [
    { key: "cards", label: "Cartes", icon: "▦" },
    { key: "table", label: "Tableau", icon: "☰" },
    { key: "compact", label: "Compact", icon: "≣" },
  ];
  const ViewSwitcher = () => (
    <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1">
      {VIEW_OPTIONS.map((o) => (
        <button
          key={o.key}
          onClick={() => setViewMode(o.key)}
          title={o.label}
          className={`px-2.5 py-1.5 rounded-lg text-sm font-semibold transition-all ${viewMode === o.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
        >
          <span className="mr-1">{o.icon}</span>{o.label}
        </button>
      ))}
    </div>
  );

  const StatusBadge = ({ statut }) => {
    const map = {
      approuve: "bg-green-100 text-green-700",
      en_attente: "bg-yellow-100 text-yellow-700",
      refuse: "bg-red-100 text-red-700",
    };
    return (
      <span className={`px-2.5 py-1 inline-flex text-[11px] leading-5 font-bold rounded-full ${map[statut] || "bg-slate-100 text-slate-600"}`}>
        {statut === "approuve" ? "✓ Approuvé" : statut === "en_attente" ? "⏳ En attente" : statut || "—"}
      </span>
    );
  };

  const Stars = ({ note }) => (
    <span className="text-amber-400 tracking-tight">{"★".repeat(note || 0)}<span className="text-slate-200">{"★".repeat(Math.max(0, 5 - (note || 0)))}</span></span>
  );

  const renderActions = (review) => (
    <>
      {review.statut === "en_attente" && (
        <button onClick={() => handleApprove(review._id)} className="text-emerald-700 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-all text-xs font-semibold">✓ Approuver</button>
      )}
      <button onClick={() => handleDeleteClick(review._id)} className="text-red-700 px-3 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-all text-xs font-semibold">🗑️ Supprimer</button>
    </>
  );

  if (error) return <div className="text-red-500 bg-red-100 p-4 rounded-lg">{error}</div>;

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-2xl font-bold text-slate-800">Gestion des Avis</h2>
        {!loading && reviews.length > 0 && (
          <div className="flex items-center gap-3">
            <p className="text-sm text-slate-500"><span className="font-bold text-slate-700">{reviews.length}</span> avis</p>
            <ViewSwitcher />
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <style>{`.nz-skel{position:relative;overflow:hidden;background:#eef2f7}.nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}@keyframes nz-shimmer{100%{transform:translateX(100%)}}`}</style>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-100 p-5 space-y-3">
              <div className="h-4 w-1/3 rounded nz-skel" />
              <div className="h-3 w-1/4 rounded nz-skel" />
              <div className="h-3 w-full rounded nz-skel" />
              <div className="h-3 w-5/6 rounded nz-skel" />
            </div>
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-100">
          <div className="text-5xl mb-3">💬</div>
          <p className="text-slate-500">Aucun avis à afficher pour le moment.</p>
        </div>
      ) : viewMode === "table" ? (
        <div className="bg-white shadow-sm border border-slate-100 rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  {["Utilisateur", "Note", "Commentaire", "Statut", "Actions"].map((h) => (
                    <th key={h} className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-400 uppercase tracking-[0.08em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {reviews.map((review) => (
                  <tr key={review._id} className="hover:bg-orange-50/40 transition-colors">
                    <td className="px-6 py-4 text-sm font-semibold text-slate-800">{review.utilisateur?.nom || "Anonyme"}</td>
                    <td className="px-6 py-4 text-sm"><Stars note={review.note} /></td>
                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{review.commentaire}</td>
                    <td className="px-6 py-4"><StatusBadge statut={review.statut} /></td>
                    <td className="px-6 py-4"><div className="flex gap-2">{renderActions(review)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : viewMode === "compact" ? (
        <div className="bg-white shadow-sm border border-slate-100 rounded-3xl overflow-hidden divide-y divide-slate-100">
          {reviews.map((review) => (
            <div key={review._id} className="flex items-center gap-3 px-4 lg:px-6 py-3 hover:bg-orange-50/40 transition-colors">
              <div className="w-10 h-10 flex-none rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center font-bold text-sm">{(review.utilisateur?.nom || "?").charAt(0).toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 text-sm truncate">{review.utilisateur?.nom || "Anonyme"} <span className="ml-1"><Stars note={review.note} /></span></p>
                <p className="text-xs text-slate-400 truncate">{review.commentaire}</p>
              </div>
              <StatusBadge statut={review.statut} />
              <div className="flex gap-1.5 flex-none">{renderActions(review)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
          {reviews.map((review) => (
            <div key={review._id} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 flex-none rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center font-black">{(review.utilisateur?.nom || "?").charAt(0).toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 truncate">{review.utilisateur?.nom || "Anonyme"}</p>
                    <p className="text-sm"><Stars note={review.note} /></p>
                  </div>
                </div>
                <StatusBadge statut={review.statut} />
              </div>
              <p className="mt-3 text-sm text-slate-600 flex-1 italic">“{review.commentaire}”</p>
              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">{renderActions(review)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirmer la suppression"
        message="Êtes-vous sûr de vouloir supprimer cet avis ? Cette action est irréversible."
      />

      {/* Pagination Controls */}
      <div className="px-5 py-5 bg-white border-t flex flex-col xs:flex-row items-center xs:justify-between">
        <div className="inline-flex mt-2 xs:mt-0">
          <button 
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} 
            disabled={pagination.page <= 1}
            className="text-sm bg-slate-300 hover:bg-slate-400 text-slate-800 font-semibold py-2 px-4 rounded-l disabled:opacity-50">
              Précédent
          </button>
          <button 
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} 
            disabled={pagination.page >= pagination.totalPages}
            className="text-sm bg-slate-300 hover:bg-slate-400 text-slate-800 font-semibold py-2 px-4 rounded-r disabled:opacity-50">
              Suivant
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewManager;